import React from 'react';
import { DEFAULT_RECIPIENT, LetterData } from '../../../types/letter';

export interface LetterTemplateProps {
    data: LetterData;
    showPlaceholders?: boolean;
}

function useLetterText(data: LetterData, showPlaceholders?: boolean) {
    const a = data.answers || ({} as LetterData['answers']);
    const c = data.content || ({} as LetterData['content']);
    const firstName = a.firstName || (showPlaceholders ? 'Jean' : '');
    const lastName = a.lastName || (showPlaceholders ? 'Kouassi' : '');
    const email = a.email || (showPlaceholders ? 'jean.kouassi@email.ci' : '');
    const phone = a.phone || (showPlaceholders ? '+225 07 00 00 00 00' : '');
    const address = a.address || (showPlaceholders ? 'Cocody, Abidjan' : '');
    const company = a.company || (showPlaceholders ? 'Nom de l’entreprise' : '');
    const city = a.city || (showPlaceholders ? 'Abidjan' : '');
    const today = new Date().toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    const placeAndDate = `${city || 'Abidjan'}, le ${today}`;
    const greeting = c.greeting || (showPlaceholders ? 'Madame, Monsieur,' : '');
    const introduction =
        c.introduction ||
        (showPlaceholders
            ? 'C’est avec un vif intérêt que je vous adresse ma candidature pour le poste proposé.'
            : '');
    const body =
        c.body ||
        (showPlaceholders
            ? 'Fort d’une expérience solide sur le marché ivoirien, je souhaite mettre mes compétences à votre service.\n\nMon parcours m’a permis de développer une grande capacité d’adaptation et un sens aigu du résultat.'
            : '');
    const gap = c.gap || '';
    const invitation =
        c.invitation ||
        (showPlaceholders
            ? 'Dans l’attente d’une réponse de votre part, je vous prie d’agréer, Madame, Monsieur, l’expression de mes salutations distinguées.'
            : '');
    const conclusion =
        c.conclusion ||
        (showPlaceholders
            ? 'Je vous prie d’agréer, Madame, Monsieur, l’expression de mes salutations distinguées.'
            : '');
    const signature = c.signature || `${firstName} ${lastName}`.trim();
    const signatureImage = c.signatureImage || '';
    const recipient =
        (c.recipient && c.recipient.trim()) ||
        (showPlaceholders ? 'Nom du destinataire ou service' : DEFAULT_RECIPIENT);
    const autoSubject = a.jobTitle
        ? `Objet : Candidature au poste de ${a.jobTitle}${a.company ? ` — ${a.company}` : ''}`
        : 'Objet : Candidature spontanée';
    const subject = (c.subject && c.subject.trim()) || autoSubject;
    const senderNameLine =
        lastName || firstName
            ? `${(lastName || '').toUpperCase()}${lastName && firstName ? ' ' : ''}${firstName}`.trim()
            : showPlaceholders
              ? 'NOM et Prénom'
              : '';
    const signNameLine =
        firstName || lastName
            ? `${firstName}${firstName && lastName ? ' ' : ''}${(lastName || '').toUpperCase()}`.trim()
            : showPlaceholders
              ? 'Prénom et NOM'
              : '';
    return {
        firstName,
        lastName,
        email,
        phone,
        address,
        company,
        city,
        today,
        placeAndDate,
        greeting,
        introduction,
        body,
        gap,
        invitation,
        conclusion,
        signature,
        signatureImage,
        recipient,
        subject,
        senderNameLine,
        signNameLine,
        primary: data.style?.primaryColor || '#0d9488',
        font: data.style?.fontFamily || 'Georgia, serif',
    };
}

type LetterText = ReturnType<typeof useLetterText>;

function DrawnSignature({
    t,
    className = '',
    align = 'left',
}: {
    t: LetterText;
    className?: string;
    align?: 'left' | 'right';
}) {
    return (
        <div className={`${align === 'right' ? 'text-right' : 'text-left'} ${className}`}>
            <p>{t.signNameLine || t.signature}</p>
            {t.signatureImage ? (
                <img
                    src={t.signatureImage}
                    alt="Signature"
                    className={`mt-2 h-14 max-w-[200px] object-contain ${
                        align === 'right' ? 'ml-auto' : ''
                    }`}
                />
            ) : (
                <p className="mt-1 text-slate-400 italic text-[11px]">Signature</p>
            )}
        </div>
    );
}

/** Mise en page française classique (expéditeur gauche / destinataire droite). */
export const LetterFormelTemplate: React.FC<LetterTemplateProps> = ({ data, showPlaceholders }) => {
    const t = useLetterText(data, showPlaceholders);

    return (
        <div
            className="cv-page-sheet h-[297mm] max-h-[297mm] w-full bg-white overflow-hidden px-[18mm] py-[16mm] text-[12px] leading-[1.55] text-black"
            style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}
        >
            <div className="flex justify-between items-start gap-8">
                <div className="space-y-0.5 max-w-[48%]">
                    <p className="font-bold">{t.senderNameLine}</p>
                    {t.address && <p>{t.address}</p>}
                    {t.city && !t.address?.toLowerCase().includes(t.city.toLowerCase()) && (
                        <p>{t.city}</p>
                    )}
                    {t.phone && <p>{t.phone}</p>}
                    {t.email && <p>{t.email}</p>}
                </div>
                <div className="text-right space-y-0.5 max-w-[48%] pt-6">
                    {showPlaceholders ? (
                        <>
                            <p>Nom de l’entreprise</p>
                            <p>{t.recipient}</p>
                            <p>Adresse de l’entreprise</p>
                            <p>Code postal / Ville</p>
                        </>
                    ) : (
                        <>
                            {t.company && <p>{t.company}</p>}
                            <p>{t.recipient}</p>
                            {t.city && <p>{t.city}</p>}
                        </>
                    )}
                </div>
            </div>

            <p className="text-right mt-10 mb-8">{t.placeAndDate}</p>

            {t.subject && <p className="font-bold mb-6">{t.subject}</p>}

            <p className="font-bold mb-5">{t.greeting}</p>

            <div className="space-y-4 text-left">
                {t.introduction && <p className="whitespace-pre-line">{t.introduction}</p>}
                {t.body && <p className="whitespace-pre-line">{t.body}</p>}
                {t.gap && <p className="whitespace-pre-line">{t.gap}</p>}
                {t.invitation && <p className="whitespace-pre-line">{t.invitation}</p>}
                {t.conclusion && t.conclusion !== t.invitation && (
                    <p className="whitespace-pre-line">{t.conclusion}</p>
                )}
            </div>

            <DrawnSignature t={t} className="mt-12 ml-auto w-[42%]" />
        </div>
    );
};

export const LetterClassicTemplate: React.FC<LetterTemplateProps> = ({ data, showPlaceholders }) => {
    const t = useLetterText(data, showPlaceholders);
    return (
        <div
            className="cv-page-sheet h-[297mm] max-h-[297mm] w-full bg-white overflow-hidden px-14 py-12 text-[12.5px] leading-relaxed text-slate-800"
            style={{ fontFamily: t.font }}
        >
            <div className="flex justify-between items-start mb-10">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight" style={{ color: t.primary }}>
                        {t.firstName} {t.lastName}
                    </h1>
                    <div className="mt-2 text-[11px] text-slate-500 space-y-0.5">
                        {t.address && <p>{t.address}</p>}
                        {t.phone && <p>{t.phone}</p>}
                        {t.email && <p>{t.email}</p>}
                    </div>
                </div>
                <div className="text-right text-[11px] text-slate-500 space-y-0.5">
                    {t.company && <p className="font-medium text-slate-700">{t.company}</p>}
                    <p>{t.recipient}</p>
                    <p className="mt-3">{t.today}</p>
                </div>
            </div>
            <div className="h-0.5 mb-8" style={{ backgroundColor: t.primary }} />
            {t.subject && <p className="font-semibold mb-6">{t.subject}</p>}
            <p className="mb-4">{t.greeting}</p>
            <p className="mb-4 whitespace-pre-line">{t.introduction}</p>
            <p className="mb-4 whitespace-pre-line">{t.body}</p>
            {t.gap && <p className="mb-4 whitespace-pre-line">{t.gap}</p>}
            <p className="mb-4 whitespace-pre-line">{t.invitation}</p>
            <p className="mb-8 whitespace-pre-line">{t.conclusion}</p>
            <DrawnSignature t={t} />
        </div>
    );
};

export const LetterCorporateTemplate: React.FC<LetterTemplateProps> = ({ data, showPlaceholders }) => {
    const t = useLetterText(data, showPlaceholders);
    const color = data.style?.primaryColor || '#1e3a5f';
    return (
        <div
            className="cv-page-sheet h-[297mm] max-h-[297mm] w-full bg-white overflow-hidden text-[12.5px] leading-relaxed text-slate-800"
            style={{ fontFamily: t.font }}
        >
            <div className="px-12 py-6 text-white" style={{ backgroundColor: color }}>
                <h1 className="text-2xl font-bold uppercase tracking-wide">
                    {t.firstName} {t.lastName}
                </h1>
                <div className="mt-2 flex flex-wrap gap-4 text-[11px] opacity-90">
                    {t.phone && <span>{t.phone}</span>}
                    {t.email && <span>{t.email}</span>}
                    {t.address && <span>{t.address}</span>}
                </div>
            </div>
            <div className="px-12 py-10">
                <div className="text-right text-[11px] text-slate-500 mb-8 space-y-0.5">
                    {t.company && <p className="font-medium text-slate-700">{t.company}</p>}
                    <p>{t.recipient}</p>
                    <p className="mt-2">{t.today}</p>
                </div>
                {t.subject && (
                    <p className="font-bold mb-6 uppercase text-[11px] tracking-wide" style={{ color }}>
                        {t.subject}
                    </p>
                )}
                <p className="mb-4">{t.greeting}</p>
                <p className="mb-4 whitespace-pre-line">{t.introduction}</p>
                <p className="mb-4 whitespace-pre-line">{t.body}</p>
                {t.gap && <p className="mb-4 whitespace-pre-line">{t.gap}</p>}
                <p className="mb-4 whitespace-pre-line">{t.invitation}</p>
                <p className="mb-10 whitespace-pre-line">{t.conclusion}</p>
                <DrawnSignature t={t} />
            </div>
        </div>
    );
};

export const LetterModernTemplate: React.FC<LetterTemplateProps> = ({ data, showPlaceholders }) => {
    const t = useLetterText(data, showPlaceholders);
    const color = data.style?.primaryColor || '#c2410c';
    return (
        <div
            className="cv-page-sheet h-[297mm] max-h-[297mm] w-full bg-white overflow-hidden flex text-[12.5px] leading-relaxed text-slate-800"
            style={{ fontFamily: t.font }}
        >
            <div className="w-2 shrink-0" style={{ backgroundColor: color }} />
            <div className="flex-1 px-12 py-12">
                <div className="flex justify-between items-end mb-2">
                    <h1 className="text-3xl font-bold text-slate-900">
                        {t.firstName}{' '}
                        <span style={{ color }}>{t.lastName}</span>
                    </h1>
                    <div className="text-right text-[11px] text-slate-400 space-y-0.5">
                        {t.company && <p className="text-slate-600">{t.company}</p>}
                        <p>{t.recipient}</p>
                        <p>{t.today}</p>
                    </div>
                </div>
                <div className="text-[11px] text-slate-500 mb-8 flex gap-3 flex-wrap">
                    {t.email && <span>{t.email}</span>}
                    {t.phone && <span>· {t.phone}</span>}
                    {t.address && <span>· {t.address}</span>}
                </div>
                {t.subject && <p className="font-semibold mb-6">{t.subject}</p>}
                <p className="mb-4">{t.greeting}</p>
                <p className="mb-4 whitespace-pre-line">{t.introduction}</p>
                <p className="mb-4 whitespace-pre-line">{t.body}</p>
                {t.gap && <p className="mb-4 whitespace-pre-line">{t.gap}</p>}
                <p className="mb-4 whitespace-pre-line">{t.invitation}</p>
                <p className="mb-10 whitespace-pre-line">{t.conclusion}</p>
                <DrawnSignature t={t} />
            </div>
        </div>
    );
};

export const LetterIvoireTemplate: React.FC<LetterTemplateProps> = ({ data, showPlaceholders }) => {
    const t = useLetterText(data, showPlaceholders);
    const color = data.style?.primaryColor || '#166534';
    return (
        <div
            className="cv-page-sheet h-[297mm] max-h-[297mm] w-full bg-[#fafaf7] overflow-hidden text-[12.5px] leading-relaxed text-slate-800"
            style={{ fontFamily: t.font }}
        >
            <div className="h-3 flex">
                <div className="flex-1" style={{ backgroundColor: color }} />
                <div className="w-1/3 bg-amber-500" />
            </div>
            <div className="px-14 py-10">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold tracking-wide uppercase" style={{ color }}>
                        {t.firstName} {t.lastName}
                    </h1>
                    <div className="mt-2 text-[11px] text-slate-500 flex justify-center gap-3 flex-wrap">
                        {t.address && <span>{t.address}</span>}
                        {t.phone && <span>| {t.phone}</span>}
                        {t.email && <span>| {t.email}</span>}
                    </div>
                    <div className="mx-auto mt-4 h-px w-24 bg-amber-500" />
                </div>
                <div className="text-right text-[11px] text-slate-500 mb-6 space-y-0.5">
                    {t.company && <p className="text-slate-700">{t.company}</p>}
                    <p>{t.recipient}</p>
                    <p className="mt-2">{t.today}</p>
                </div>
                {t.subject && <p className="font-semibold mb-6">{t.subject}</p>}
                <p className="mb-4">{t.greeting}</p>
                <p className="mb-4 whitespace-pre-line">{t.introduction}</p>
                <p className="mb-4 whitespace-pre-line">{t.body}</p>
                {t.gap && <p className="mb-4 whitespace-pre-line">{t.gap}</p>}
                <p className="mb-4 whitespace-pre-line">{t.invitation}</p>
                <p className="mb-10 whitespace-pre-line">{t.conclusion}</p>
                <DrawnSignature t={t} align="right" />
            </div>
        </div>
    );
};
